// Thin REST client.  In production, requests go through the same-origin
// reverse proxy (nginx) which injects the X-API-Key header on the way to
// the FastAPI backend.  The browser only sends ``Content-Type`` and never
// sees (or hard-codes) the API key.
//
// For non-prod builds where ``VITE_API_KEY`` is provided at build time
// (legacy fallback), we still attach it as a header.  This keeps local
// dev tools working but is intentionally NOT used by the production
// hotfix build path (``VITE_API_KEY`` is left empty in the production
// .env so the bundle contains no secrets).

import type {
  ConcurrencyConfig,
  ConcurrencyConfigResponse,
  CostRow,
  MessageCreateResponse,
  Project,
  ProjectLaneSummary,
  ReviewDecisionPayload,
  RollbackRequest,
  RollbackResponse,
  Run,
  RunStep,
  Task,
  TaskTrace,
} from './types';

const BASE_URL: string = (import.meta.env.VITE_API_BASE_URL as string) || '';
const API_KEY: string = (import.meta.env.VITE_API_KEY as string) || '';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type ProviderStatusType =
  | 'active'
  | 'disabled'
  | 'rate_limited'
  | 'unconfigured';

export interface ProviderStatus {
  provider: string;
  display_name: string;
  status: ProviderStatusType;
  configured: boolean;
  fingerprint: string | null;
  model: string | null;
  base_url: string | null;
  key_count: number;
  last_used_at: string | null;
}

export interface SettingsProvidersResponse {
  providers: ProviderStatus[];
}

export interface SaveProviderKeyPayload {
  api_key: string;
  model?: string;
  base_url?: string;
}

export interface SaveProviderKeyResponse {
  provider: string;
  configured: boolean;
  fingerprint: string | null;
  model: string | null;
  base_url: string | null;
  status: string;
}

async function errorMessage(resp: Response): Promise<string> {
  const text = await resp.text();
  if (!text) return `${resp.status} ${resp.statusText}`;
  const sanitize = (message: string) => {
    if (/invalid_vault_ref|missing_vault_ref|vault_ref/i.test(message)) {
      return '\u6240\u9009\u8bbf\u95ee\u914d\u7f6e\u4e0d\u53ef\u7528\uff0c\u8bf7\u91cd\u65b0\u9009\u62e9\u6216\u66f4\u65b0\u6388\u6743\u3002';
    }
    if (/ghp_[a-z0-9_]+|github_pat_[a-z0-9_]+|authorization|bearer\s+/i.test(message)) {
      return '\u51ed\u636e\u6821\u9a8c\u5931\u8d25\uff0c\u8bf7\u66f4\u65b0\u6388\u6743\u540e\u91cd\u8bd5\u3002';
    }
    if (/x-api-key|api key|unauthorized|not authenticated/i.test(message)) {
      return '\u767b\u5f55\u5df2\u5931\u6548\u6216\u670d\u52a1\u9274\u6743\u672a\u901a\u8fc7\uff0c\u8bf7\u91cd\u65b0\u767b\u5f55\u540e\u91cd\u8bd5\u3002';
    }
    return message;
  };
  // Backend structured-detail contract: HTTPException(detail={_code, _detail}).
  // The page must see the human-readable ``_detail`` (and ideally the
  // ``_code``), not a stringified JSON blob.
  const formatStructured = (obj: Record<string, unknown>): string => {
    const code = typeof obj._code === 'string' ? obj._code : '';
    const detail = typeof obj._detail === 'string' ? obj._detail : '';
    if (code && detail) return `[${code}] ${detail}`;
    if (detail) return detail;
    if (code) return code;
    return sanitize(JSON.stringify(obj));
  };
  try {
    const parsed = JSON.parse(text) as { detail?: unknown; message?: unknown };
    const detail = parsed.detail ?? parsed.message;
    if (typeof detail === 'string') return sanitize(detail);
    if (detail && typeof detail === 'object') {
      return formatStructured(detail as Record<string, unknown>);
    }
    if (detail) return sanitize(JSON.stringify(detail));
  } catch {
    return sanitize(text);
  }
  return sanitize(text);
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Build headers.  In production we deliberately do NOT set X-API-Key
  // client-side — the same-origin reverse proxy (nginx) injects it.
  // When ``VITE_API_KEY`` is configured at build time (legacy / local
  // dev) we still attach it as a header for backward compatibility.
  //
  // In non-proxy local dev where ``VITE_API_KEY`` is also empty we fail
  // loudly so misconfiguration surfaces immediately instead of producing
  // mysterious 401s that look like server bugs.
  if (!API_KEY && !navigator?.onLine) {
    // Network is offline — there's nothing to call; let the browser surface
    // the offline error so the user gets a sensible message.
  }
  const headers = new Headers(init.headers);
  // IMPORTANT: explicit ``charset=utf-8`` on every JSON request so
  // Chinese / CJK payloads (project names, blueprint drafts, etc.)
  // round-trip the proxy / uvicorn / Pydantic stack without the
  // browser's default ``application/json`` (no charset) being
  // interpreted by some intermediate component as ISO-8859-1.
  // The exact byte-level root cause of the ``????`` mojibake is
  // NOT yet captured on the wire (no mitmproxy / tcpdump trace);
  // this explicit charset is the safest mitigation until the real
  // cause is reproduced.  See docs/run_reports/2026-07-06-AB-deploy-runbook.md
  // for the re-diagnosis protocol if mojibake still shows up after
  // deploy.
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json; charset=utf-8');
  } else if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json; charset=utf-8');
  }
  if (API_KEY) {
    headers.set('X-API-Key', API_KEY);
  } else {
    // No client-side key configured.  In production the reverse proxy
    // injects X-API-Key on the way to the backend.  In a bare local dev
    // build without the proxy, requests will be rejected with 401 —
    // surface that as a configuration error so operators see it.
    console.warn(
      "Frontend API key is not configured (VITE_API_KEY is empty); "
      + "requests rely on the reverse proxy to inject X-API-Key.",
    );
  }
  const url = `${BASE_URL}${path}`;
  const resp = await fetch(url, { ...init, headers, credentials: 'same-origin' });
  if (!resp.ok) {
    // Preserve the structured ``detail`` payload alongside the
    // human-readable message so callers (e.g. ``confirmTaskPlan``)
    // can recover ``detail.quality_gate`` from a 409 response and
    // refresh the gate-B panel without a full page reload.
    let detail: Record<string, unknown> | undefined;
    try {
      const txt = await resp.clone().text();
      const parsed = JSON.parse(txt);
      if (parsed && typeof parsed === 'object' && parsed.detail) {
        detail = parsed.detail as Record<string, unknown>;
      } else if (parsed && typeof parsed === 'object') {
        detail = parsed as Record<string, unknown>;
      }
    } catch {
      // non-JSON error body; detail stays undefined
    }
    throw new ApiError(resp.status, await errorMessage(resp), detail);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

// ─── Projects ───────────────────────────────────────────────────────────
export const projectsApi = {
  // Default: view=active (NOT archived AND NOT deleted).  Pass
  // `?view=archived|recycle-bin|all` explicitly when needed.
  list: (): Promise<Project[]> =>
    request<Project[]>('/api/v1/projects?view=active'),
  listWithQuery: (q: string): Promise<Project[]> =>
    request<Project[]>(`/api/v1/projects${q.startsWith('?') ? q : `?${q}`}`),
  create: (p: {
    slug: string;
    name: string;
    local_path: string;
    repo_url?: string;
    default_branch?: string;
    monthly_budget_usd?: number;
  }): Promise<Project> =>
    request<Project>('/api/v1/projects', {
      method: 'POST',
      body: JSON.stringify(p),
    }),
};

// ─── Tasks ──────────────────────────────────────────────────────────────
export const tasksApi = {
  list: (filters?: { project_id?: string; status?: string; view?: string }): Promise<Task[]> => {
    const qs = new URLSearchParams();
    if (filters?.project_id) qs.set('project_id', filters.project_id);
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.view) qs.set('view', filters.view);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<Task[]>(`/api/v1/tasks${suffix}`);
  },
  listWithQuery: (q: string): Promise<Task[]> =>
    request<Task[]>(`/api/v1/tasks${q.startsWith('?') ? q : `?${q}`}`),
  get: (id: string): Promise<Task> => request<Task>(`/api/v1/tasks/${id}`),
  runs: (id: string): Promise<Run[]> => request<Run[]>(`/api/v1/tasks/${id}/runs`),
  retry: (id: string): Promise<Task> =>
    request<Task>(`/api/v1/tasks/${id}/retry`, { method: 'POST' }),
  create: (t: {
    project_id: string;
    task_id_slug: string;
    title: string;
    description?: string;
    goal?: string;
    priority?: number;
  }): Promise<Task> =>
    request<Task>('/api/v1/tasks', {
      method: 'POST',
      body: JSON.stringify(t),
    }),
  costs: (id: string): Promise<CostRow[]> =>
    request<CostRow[]>(`/api/v1/tasks/${id}/costs`),
  // v0.8.1 — Step-by-step execution trace for a task's latest run.
  // Backend: api/routes/tasks.py:345 (GET /{task_id}/trace, v0.6-beta-3).
  trace: (id: string): Promise<TaskTrace> =>
    request<TaskTrace>(`/api/v1/tasks/${id}/trace`),
  artifact: (id: string): Promise<{
    available: boolean;
    task_id: string;
    project_id: string;
    preview_url: string;
    download_url: string;
    entry_file: string;
    root_kind: string;
  }> => request(`/api/v1/tasks/${id}/artifact`),
  // v0.8.1 — Roll back a real-git-workspace run to its pre-execution state.
  // Only effective when the latest run's rollback_status === 'available'.
  // Backend: api/routes/tasks.py:489 (POST /{task_id}/rollback, v0.6-beta-4).
  rollback: (id: string, payload?: RollbackRequest): Promise<RollbackResponse> =>
    request<RollbackResponse>(`/api/v1/tasks/${id}/rollback`, {
      method: 'POST',
      body: JSON.stringify(payload ?? {}),
    }),
  // v0.8.4 — Human Gate: submit a review decision (approve / reject / require_fix).
  // Backend: api/routes/tasks.py (POST /{task_id}/review-decision).
  // Phase 3: pass ``project_id`` so the route's cross-project guard
  // rejects mismatches before any state mutation.
  reviewDecision: (
    id: string,
    payload: ReviewDecisionPayload,
    projectId?: string,
  ): Promise<Task> => {
    const qs = new URLSearchParams();
    if (projectId) qs.set('project_id', projectId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<Task>(`/api/v1/tasks/${id}/review-decision${suffix}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // v0.9.5 — Review Queue shortcuts
  approve: (id: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = new URLSearchParams();
    if (projectId) qs.set('project_id', projectId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<Record<string, unknown>>(`/api/v1/tasks/${id}/approve${suffix}`, { method: 'POST' });
  },
  reject: (id: string, reason?: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = new URLSearchParams();
    if (reason) qs.set('reason', reason);
    if (projectId) qs.set('project_id', projectId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<Record<string, unknown>>(`/api/v1/tasks/${id}/reject${suffix}`, { method: 'POST' });
  },
  requireFix: (id: string, reason: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = new URLSearchParams();
    qs.set('reason', reason);
    if (projectId) qs.set('project_id', projectId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<Record<string, unknown>>(`/api/v1/tasks/${id}/require-fix${suffix}`, { method: 'POST' });
  },
  getReview: (id: string, projectId?: string): Promise<Record<string, unknown>> => {
    const qs = new URLSearchParams();
    if (projectId) qs.set('project_id', projectId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<Record<string, unknown>>(`/api/v1/tasks/${id}/review${suffix}`);
  },
};

// v0.9.5 — Review Queue
export const reviewQueueApi = {
  // Phase 3: optional ``project_id`` scopes the queue to a single
  // project.  When omitted, the list is cross-project.
  list: (projectId?: string): Promise<Record<string, unknown>[]> => {
    const qs = new URLSearchParams();
    if (projectId) qs.set('project_id', projectId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<Record<string, unknown>[]>(`/api/v1/review-queue${suffix}`);
  },
};

// ─── Phase 3 — Project Lanes & Concurrency Config ─────────────────────
export const projectLanesApi = {
  list: (view: 'active' | 'all' = 'active'): Promise<ProjectLaneSummary[]> => {
    const qs = new URLSearchParams({ view });
    return request<ProjectLaneSummary[]>(`/api/v1/project-lanes?${qs.toString()}`);
  },
  get: (projectId: string): Promise<ProjectLaneSummary & { slot_budget: ConcurrencyConfig }> =>
    request(`/api/v1/projects/${encodeURIComponent(projectId)}/lane`),
  concurrency: (): Promise<ConcurrencyConfigResponse> =>
    request<ConcurrencyConfigResponse>('/api/v1/project-lanes/concurrency'),
};

// ─── Message Gateway ────────────────────────────────────────────────────
export const messagesApi = {
  createTask: (content: string, source = 'home_ui'): Promise<MessageCreateResponse> =>
    request<MessageCreateResponse>('/api/v1/messages', {
      method: 'POST',
      body: JSON.stringify({
        source,
        content,
        decomposition_mode: 'single',
        metadata: {
          source_ui: source,
        },
      }),
    }),
};

// ─── Runs ───────────────────────────────────────────────────────────────
export const settingsApi = {
  listProviders: (): Promise<SettingsProvidersResponse> =>
    request<SettingsProvidersResponse>('/api/v1/settings/providers'),
  saveProviderKey: (
    provider: string,
    payload: SaveProviderKeyPayload,
  ): Promise<SaveProviderKeyResponse> =>
    request<SaveProviderKeyResponse>(
      `/api/v1/settings/providers/${encodeURIComponent(provider)}/key`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),
  // v0.9 — Enable / disable a provider without re-saving the key.
  patchProviderStatus: (
    provider: string,
    status: 'active' | 'disabled' | 'rate_limited',
  ): Promise<ProviderStatus> =>
    request<ProviderStatus>(
      `/api/v1/settings/providers/${encodeURIComponent(provider)}/status`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
    ),
  // v0.9 — Read / write the global + per-task-type model policy.
  getModelPolicy: (): Promise<import('./types').ModelPolicy> =>
    request<import('./types').ModelPolicy>('/api/v1/settings/model-policy'),
  putModelPolicy: (
    payload: import('./types').ModelPolicyUpdatePayload,
  ): Promise<import('./types').ModelPolicy> =>
    request<import('./types').ModelPolicy>('/api/v1/settings/model-policy', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
};

// ─── v0.9.22 — Access Profiles ────────────────────────────────────────────────────
export const accessProfilesApi = {
  list: (): Promise<{ profiles: Record<string, unknown>[] }> =>
    request<{ profiles: Record<string, unknown>[] }>('/api/v1/access-profiles'),
  createVps: (payload: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>('/api/v1/access-profiles/vps', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  updateVps: (id: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/access-profiles/vps/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(payload),
    }),
  checkVps: (id: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/access-profiles/vps/${encodeURIComponent(id)}/check`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  createGithub: (payload: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>('/api/v1/access-profiles/github', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  updateGithub: (id: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/access-profiles/github/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(payload),
    }),
  checkGithub: (id: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/access-profiles/github/${encodeURIComponent(id)}/check`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
};

// ─── v0.9 — Task Packages ───────────────────────────────────────────────
export const taskPackagesApi = {
  generate: (payload: import('./types').TaskPackageRequest): Promise<import('./types').TaskPackage> =>
    request<import('./types').TaskPackage>('/api/v1/task-packages/generate', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// ─── v0.9 — Project Archive ─────────────────────────────────────────────
export const projectArchiveApi = {
  get: (projectId: string): Promise<import('./types').ProjectArchive> =>
    request<import('./types').ProjectArchive>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/archive`,
    ),
};

export const runsApi = {
  get: (id: string): Promise<Run> => request<Run>(`/api/v1/runs/${id}`),
  steps: (id: string): Promise<RunStep[]> =>
    request<RunStep[]>(`/api/v1/runs/${id}/steps`),
  costs: (id: string): Promise<CostRow[]> =>
    request<CostRow[]>(`/api/v1/runs/${id}/costs`),
};

export async function latestRunIdForTask(taskId: string): Promise<string | null> {
  const runs = await tasksApi.runs(taskId).catch(() => [] as Run[]);
  return runs[0]?.id ?? null;
}

// ─── v0.9.1 — Project Takeover ────────────────────────────────────────────
export const projectTakeoverApi = {
  scan: (payload: import('./types').TakeoverScanRequest): Promise<import('./types').TakeoverReport> =>
    request<import('./types').TakeoverReport>('/api/v1/project-takeover/scan', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// ─── v0.9.2 — Context Snapshots ──────────────────────────────────────────
export const contextSnapshotApi = {
  create: (payload: Record<string, unknown>): Promise<import('./types').ContextSnapshotOut> =>
    request<import('./types').ContextSnapshotOut>('/api/v1/context-snapshots', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  get: (id: string): Promise<import('./types').ContextSnapshotOut> =>
    request<import('./types').ContextSnapshotOut>(`/api/v1/context-snapshots/${id}`),
  listForProject: (projectId: string): Promise<import('./types').ContextSnapshotOut[]> =>
    request<import('./types').ContextSnapshotOut[]>(`/api/v1/projects/${encodeURIComponent(projectId)}/context-snapshots`),
  fromTakeover: (payload: import('./types').ContextSnapshotFromTakeoverRequest): Promise<import('./types').ContextSnapshotOut> =>
    request<import('./types').ContextSnapshotOut>('/api/v1/context-snapshots/from-takeover', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};


// ─── v0.9.6 — Command Center ────────────────────────────────────────────────
export const commandCenterApi = {
  get: (projectId: string): Promise<import('./types').CommandCenterOut> =>
    request<import('./types').CommandCenterOut>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/command-center`,
    ),
};


// ─── v0.9.23 — Model Assignment ─────────────────────────────────────────────
export const modelAssignmentApi = {
  get: (): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>('/api/v1/model-assignment'),
  save: (payload: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>('/api/v1/model-assignment', {
      method: 'PUT', body: JSON.stringify(payload),
    }),
  presets: (): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>('/api/v1/model-assignment/presets'),
  options: (): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>('/api/v1/model-assignment/options'),
  codexCliStatus: (): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>('/api/v1/model-assignment/codex-cli/status'),
};

// ─── v0.9.23 — Live Demo Mode ───────────────────────────────────────────────
// --- Phase 2 2B P2: Live demo sessions (ephemeral) ---
// Live demo never touches projects / stages / tasks / execution pipeline.
// State stays inside live_demo_sessions and is operator-stoppable.
export type LiveDemoStatus = 'active' | 'stopped' | 'completed';
export type LiveDemoMode = 'plan' | 'demo';
export interface LiveDemoMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  ts: string;
}
export interface LiveDemoSession {
  id: string;
  created_by_admin_id: string;
  title: string;
  raw_requirement: string;
  status: LiveDemoStatus;
  mode: LiveDemoMode;            // plan (快速方案演示) | demo (快速 Demo 演示)
  planning_model_id: string | null;
  blueprint_snapshot: Record<string, unknown>;
  messages: LiveDemoMessage[];
  started_at: string;
  last_message_at: string;
  stopped_at: string | null;
  stopped_reason: string | null;
  created_at: string;
  updated_at: string;
}
export const liveDemoApi = {
  list: (status?: LiveDemoStatus, mode?: LiveDemoMode): Promise<LiveDemoSession[]> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (mode) params.set('mode', mode);
    const q = params.toString() ? `?${params.toString()}` : '';
    return request<LiveDemoSession[]>(`/api/v1/live_demo/sessions${q}`);
  },
  get: (id: string): Promise<LiveDemoSession> =>
    request<LiveDemoSession>(`/api/v1/live_demo/sessions/${id}`),
  create: (payload: {
    title?: string;
    raw_requirement: string;
    mode?: LiveDemoMode;
    planning_model_id?: string;
  }): Promise<LiveDemoSession> =>
    request<LiveDemoSession>('/api/v1/live_demo/sessions', {
      method: 'POST', body: JSON.stringify({ mode: 'plan', ...payload }),
    }),
  message: (id: string, content: string): Promise<LiveDemoSession> =>
    request<LiveDemoSession>(`/api/v1/live_demo/sessions/${id}/message`, {
      method: 'POST', body: JSON.stringify({ content }),
    }),
  listModelCalls: (id: string): Promise<ModelCallOut[]> =>
    request<ModelCallOut[]>(`/api/v1/live_demo/sessions/${id}/model-calls`),
  stop: (id: string, reason?: string): Promise<LiveDemoSession> =>
    request<LiveDemoSession>(`/api/v1/live_demo/sessions/${id}/stop`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? '' }),
    }),
  remove: (id: string): Promise<void> =>
    request<void>(`/api/v1/live_demo/sessions/${id}`, { method: 'DELETE' }),
};

// --- Phase 2 2B: Plan ingestion sessions ---
export type PlanIngestionStatus =
  | 'draft' | 'clarifying' | 'awaiting_human_review'
  | 'confirmed' | 'committing' | 'completed'
  | 'cancelled' | 'failed';
export interface PlanIngestionMessageOut {
  id: string;
  session_id: string;
  round: number;
  role: 'user' | 'planner' | 'system';
  content: string;
  sequence: number;
  created_at: string;
}
export interface PlanIngestionSessionOut {
  id: string;
  created_by_admin_id: string;
  raw_requirement: string;
  status: PlanIngestionStatus;
  clarification_round: number;
  clarify_round?: number | null;
  clarification_max_rounds: number;
  planning_model_id: string | null;
  suggested_blueprint_id: string | null;
  confirmed_blueprint_id: string | null;
  manual_outline: unknown[];
  created_project_id: string | null;
  created_stage_plan_id: string | null;
  failure_reason: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  messages?: PlanIngestionMessageOut[];
  // v1.0-rc.7 audit14 phase 3
  mock_mode: boolean;
  clarify_model_id: string | null;
  last_call_status: 'no_call' | 'real_ok' | 'real_failed' | 'mock_only' | 'real_no_call';
  clarify_status?: 'awaiting_answer' | 'analyzing' | 'sufficient' | 'failed' | string;
  current_question_id?: string | null;
  current_question?: string | null;
  recommended_answer?: string | null;
  last_request_id?: string | null;
  last_model_used?: string | null;
  last_error?: string | null;
}

export interface ModelOptionOut {
  // Stable submit-key for the planning / clarify picker.  When set
  // (real provider_models rows) the frontend must use this UUID as
  // the create-session payload's planning_model_id / clarify_model_id.
  // When null (e.g. custom_providers rows or the always-on mock), the
  // option is not submittable as a real provider id.
  provider_model_id: string | null;
  provider_name: string;
  display_name: string;
  model_id: string;
  configured: boolean;
  is_default: boolean;
  suggestion: string;
  caveat: string;
  is_mock: boolean;
  capabilities?: string[];
  execution_adapter?: string | null;
  execution_adapter_status?: string;
  execution_note?: string;
}

export interface ModelCallOut {
  id: string;
  session_id: string | null;
  live_demo_session_id: string | null;
  task_id: string | null;
  provider: string;
  model: string;
  purpose: 'clarify' | 'blueprint' | 'blueprint_repair' | 'live_plan' | 'live_demo' | 'task_plan';
  is_real: boolean;
  status: 'pending' | 'succeeded' | 'failed';
  started_at: string;
  ended_at: string | null;
  duration_ms: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_usd: number | null;
  error_summary: string | null;
  created_at: string;
}

export const planIngestionApi = {
  list: (
    status?: PlanIngestionStatus,
    filters?: { project_id?: string | null },
  ): Promise<PlanIngestionSessionOut[]> => {
    const q = new URLSearchParams();
    if (status) q.set('status', status);
    if (filters?.project_id) q.set('project_id', filters.project_id);
    const suffix = q.toString() ? `?${q.toString()}` : '';
    return request<PlanIngestionSessionOut[]>(`/api/v1/plan_ingestion/sessions${suffix}`);
  },
  get: (id: string): Promise<PlanIngestionSessionOut> =>
    request<PlanIngestionSessionOut>(`/api/v1/plan_ingestion/sessions/${id}`),
  create: (payload: {
    raw_requirement: string;
    planning_model_id?: string | null;
    clarify_model_id?: string | null;
    mock_mode?: boolean;
  }): Promise<PlanIngestionSessionOut> =>
    request<PlanIngestionSessionOut>('/api/v1/plan_ingestion/sessions', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  addMessage: (id: string, payload: { round: number; role: 'user'|'planner'|'system'; content: string; sequence?: number }): Promise<PlanIngestionMessageOut> =>
    request<PlanIngestionMessageOut>(`/api/v1/plan_ingestion/sessions/${id}/messages`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  clarifyAsk: (id: string, force_generate = false, request_id?: string): Promise<PlanIngestionSessionOut> =>
    request<PlanIngestionSessionOut>(`/api/v1/plan_ingestion/sessions/${id}/clarify/ask`, {
      method: 'POST', body: JSON.stringify({ force_generate, request_id }),
    }),
  importOutline: (id: string, outline: Record<string, unknown>): Promise<PlanIngestionSessionOut> =>
    request<PlanIngestionSessionOut>(`/api/v1/plan_ingestion/sessions/${id}/import-outline`, {
      method: 'POST', body: JSON.stringify(outline),
    }),
  getBlueprint: (id: string): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/plan_ingestion/sessions/${id}/blueprint`),
  getExecutionPlanDraft: (id: string): Promise<{
    session_id: string;
    blueprint_id: string;
    project_goal: string;
    scope: string;
    project_acceptance_criteria: string[];
    out_of_scope: string[];
    risks: string[];
      model_roles: Record<string, string>;
      routing_policy: Record<string, unknown>;
      quality_gate: { passed: boolean; status_text: string; issues: string[]; field_mapping: Record<string, string> };
      stages: Array<{
        id: string;
        phase_number: number;
        phase_name: string;
        phase_goal: string;
        module_scope: string;
        route: Record<string, unknown>;
      }>;
      tasks: Array<{
      id: string;
      phase_id: string;
      phase_number: number;
      phase_name: string;
      phase_goal: string;
      module_scope: string;
      phase_deliverables: string[];
      phase_risks: string[];
      title: string;
      objective: string;
      expected_deliverable: string;
      acceptance_criteria: Array<{ criterion: string; priority: string; sort_order: number }>;
      dependencies: string[];
      dependency_names: string[];
      difficulty: 'low' | 'medium' | 'high' | string;
      estimated_effort: 'small' | 'medium' | 'large' | string;
      risk_level: 'low' | 'medium' | 'high' | string;
      task_type: string;
      needs_terminal: boolean;
      needs_build_or_test: boolean;
      recommended_route: string;
      route_source: 'inherit_stage' | 'user_override' | 'deferred' | string;
      backup_route: string;
      recommended_reason: string;
      current_code_model: string;
      override_stage_default: boolean;
      allow_auto_upgrade: boolean;
      recommendation_reason: string;
      allow_auto_switch: boolean;
      switch_records: string[];
      executor_agent: string;
      code_model: string;
      file_tool: string;
      build_tool: string;
    }>;
  }> =>
    request(`/api/v1/plan_ingestion/sessions/${id}/execution-plan-draft`),
  updateExecutionPlanDraft: (id: string, payload: { stages?: Record<string, Record<string, unknown>>; tasks?: Record<string, Record<string, unknown>> }): Promise<any> =>
    request(`/api/v1/plan_ingestion/sessions/${id}/execution-plan-draft`, {
      method: 'PATCH', body: JSON.stringify(payload),
    }),
  editBlueprint: (id: string, patch: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/plan_ingestion/sessions/${id}/blueprint`, {
      method: 'PUT', body: JSON.stringify(patch),
    }),
  confirm: (id: string, reason?: string): Promise<PlanIngestionSessionOut> =>
    request<PlanIngestionSessionOut>(`/api/v1/plan_ingestion/sessions/${id}/confirm`, {
      method: 'POST', body: JSON.stringify({ confirmed: true, reason: reason ?? '' }),
    }),
  cancel: (id: string, reason?: string): Promise<PlanIngestionSessionOut> =>
    request<PlanIngestionSessionOut>(`/api/v1/plan_ingestion/sessions/${id}/cancel`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? '' }),
    }),
  execute: (id: string): Promise<PlanIngestionSessionOut> =>
    request<PlanIngestionSessionOut>(`/api/v1/plan_ingestion/sessions/${id}/execute`, {
      method: 'POST', body: JSON.stringify({}),
    }),
  // Start a single StageTaskPackage → real Task + Run + queue enqueue.
  startTask: (id: string, taskPackageId: string): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/plan_ingestion/sessions/${id}/start-task`, {
      method: 'POST', body: JSON.stringify({ task_package_id: taskPackageId }),
    }),
  // List the StageTaskPackages this session created (so the UI can
  // show the 「执行选中任务」 button per package).
  listTaskPackages: (id: string): Promise<Array<{
    id: string;
    stage_id: string;
    title: string;
    objective: string;
    scope: string;
    status: string;
    package_status?: string;
    execution_channel: string;
    sort_order: number;
    task_id?: string | null;
    task_status?: string | null;
    retry_count?: number;
    max_attempts?: number;
    can_retry?: boolean;
    latest_run_id?: string | null;
    latest_run_number?: number | null;
    latest_run_status?: string | null;
  }>> =>
    request(`/api/v1/plan_ingestion/sessions/${id}/task-packages`),
  // v1.0-rc.7 audit14 phase 3: model selection audit spine
  listModelOptions: (): Promise<ModelOptionOut[]> =>
    request<ModelOptionOut[]>('/api/v1/plan_ingestion/models/options'),
  listSessionModelCalls: (sessionId: string): Promise<ModelCallOut[]> =>
    request<ModelCallOut[]>(`/api/v1/plan_ingestion/sessions/${sessionId}/model-calls`),
  listTaskModelCalls: (taskId: string): Promise<ModelCallOut[]> =>
    request<ModelCallOut[]>(`/api/v1/plan_ingestion/tasks/${taskId}/model-calls`),
  updateDraftModels: (id: string, payload: { clarify_model_id?: string | null; planning_model_id?: string | null }): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/draft_plans/${id}/models`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  deleteSession: (sessionId: string): Promise<void> =>
    request<void>(`/api/v1/plan_ingestion/sessions/${sessionId}`, {
      method: 'DELETE',
    }),
};

export interface ProjectWorkbenchTask {
  package_id: string;
  id: string | null;
  title: string;
  status: string;
  pipeline?: string | null;
  can_start: boolean;
  latest_run_id?: string | null;
  latest_run_number?: number | null;
  latest_run_status?: string | null;
  retry_count?: number;
  max_attempts?: number;
  locked?: boolean;
  history?: boolean;
  package_status?: string;
  sort_order?: number;
}

export interface ProjectWorkbenchPackage {
  id: string;
  stage_id: string;
  title: string;
  objective: string;
  scope: string;
  status: string;
  package_status?: string;
  execution_channel: string;
  sort_order: number;
  task_id?: string | null;
  task_status?: string | null;
  retry_count?: number;
  max_attempts?: number;
  can_retry?: boolean;
  latest_run_id?: string | null;
  latest_run_number?: number | null;
  latest_run_status?: string | null;
  workbench_role?: 'current' | 'locked' | 'history' | 'available' | string;
  locked?: boolean;
  history?: boolean;
  pipeline?: string | null;
}

export interface ProjectWorkbenchStage {
  id: string;
  title: string;
}

export interface ProjectWorkbenchSnapshot {
  project_id: string | null;
  project_name: string | null;
  session_id: string | null;
  stage_plan_id: string | null;
  workflow_state: 'new_project' | 'requirement' | 'clarification' | 'blueprint' | 'confirmed' | 'execution' | 'review' | 'completed' | 'incomplete_project' | string;
  current_stage: ProjectWorkbenchStage | null;
  current_task: ProjectWorkbenchTask | null;
  next_action: { type: string; label: string };
  task_packages: ProjectWorkbenchPackage[];
  history_tasks: ProjectWorkbenchTask[];
  locked_tasks: ProjectWorkbenchTask[];
  error?: string | null;
}

export const projectWorkbenchApi = {
  snapshot: (filters: { project_id?: string | null; session_id?: string | null }): Promise<ProjectWorkbenchSnapshot> => {
    const q = new URLSearchParams();
    if (filters.project_id) q.set('project_id', filters.project_id);
    if (filters.session_id) q.set('session_id', filters.session_id);
    const suffix = q.toString();
    return request<ProjectWorkbenchSnapshot>(`/api/v1/project_workbench/snapshot${suffix ? `?${suffix}` : ''}`);
  },
};

export interface DemoModule {
  key: string;
  title: string;
  body: string;
}
export interface DemoModuleResponse {
  session_id: string;
  mode: 'plan' | 'demo';
  title: string;
  status: 'active' | 'stopped' | 'completed';
  is_mock: boolean;
  cards: DemoModule[];
  raw_markdown: string;
  message_count: number;
  prototype?: {
    screens: Array<{
      id: string;
      label: string;
      kind: 'form' | 'outline' | 'list' | 'gate' | string;
      fields: Array<{ label: string; value: string }>;
    }>;
  };
  handoff_payload?: {
    raw_requirement: string;
    clarification_answers: string[];
    summary: string;
    modules: string[];
    risks: string;
  };
}

// --- Phase 2 2A: Project lifecycle ---
export interface ProjectLifecycleOut {
  project_id: string;
  archived_at: string | null;
  archive_reason: string | null;
  deleted_at: string | null;
  delete_reason: string | null;
  restorable_until: string | null;
  deleted_by_admin_id: string | null;
  is_archived: boolean;
  is_deleted: boolean;
  is_restorable: boolean;
  days_until_restore_expiry: number | null;
}
export interface DeleteImpactOut {
  project_id: string;
  affected_stages: number;
  affected_tasks: number;
  affected_acceptance_records: number;
  active_running_or_awaiting_review_tasks: number;
  would_block: boolean;
  block_reason: string | null;
  blocking_task_ids: string[];
}
export interface RecycleBinEntry {
  project_id: string;
  name: string;
  slug: string;
  deleted_at: string;
  delete_reason: string | null;
  deleted_by_admin_id: string | null;
  restorable_until: string;
  days_remaining: number;
}
export interface BatchLifecycleResult {
  archived?: string[];
  deleted?: string[];
  skipped: { project_id: string; reason: string }[];
}
export const lifecycleApi = {
  get: (projectId: string): Promise<ProjectLifecycleOut> =>
    request<ProjectLifecycleOut>(`/api/v1/lifecycle/projects/${projectId}`),
  archive: (projectId: string, reason?: string): Promise<ProjectLifecycleOut> =>
    request<ProjectLifecycleOut>(`/api/v1/lifecycle/projects/${projectId}/archive`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? '' }),
    }),
  unarchive: (projectId: string): Promise<ProjectLifecycleOut> =>
    request<ProjectLifecycleOut>(`/api/v1/lifecycle/projects/${projectId}/unarchive`, {
      method: 'POST', body: JSON.stringify({}),
    }),
  deleteImpact: (projectId: string): Promise<DeleteImpactOut> =>
    request<DeleteImpactOut>(`/api/v1/lifecycle/projects/${projectId}/delete-impact`, {
      method: 'POST', body: JSON.stringify({}),
    }),
  delete: (projectId: string, reason?: string): Promise<ProjectLifecycleOut> =>
    request<ProjectLifecycleOut>(`/api/v1/lifecycle/projects/${projectId}/delete`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? '' }),
    }),
  restore: (projectId: string): Promise<ProjectLifecycleOut> =>
    request<ProjectLifecycleOut>(`/api/v1/lifecycle/projects/${projectId}/restore`, {
      method: 'POST', body: JSON.stringify({}),
    }),
  recycleBin: (limit = 50, offset = 0): Promise<RecycleBinEntry[]> =>
    request<RecycleBinEntry[]>(`/api/v1/lifecycle/recycle-bin?limit=${limit}&offset=${offset}`),
  batchArchive: (projectIds: string[], reason?: string): Promise<BatchLifecycleResult> =>
    request<BatchLifecycleResult>('/api/v1/lifecycle/batch/archive', {
      method: 'POST', body: JSON.stringify({ project_ids: projectIds, reason: reason ?? '' }),
    }),
  batchDelete: (projectIds: string[], reason?: string): Promise<BatchLifecycleResult> =>
    request<BatchLifecycleResult>('/api/v1/lifecycle/batch/delete', {
      method: 'POST', body: JSON.stringify({ project_ids: projectIds, reason: reason ?? '' }),
    }),
};

// --- Phase 2 2A: Task lifecycle (independent) ---
export interface TaskLifecycleOut {
  task_id: string;
  project_id: string;
  status: string;
  archived_at: string | null;
  archive_reason: string | null;
  deleted_at: string | null;
  delete_reason: string | null;
  restorable_until: string | null;
  deleted_by_admin_id: string | null;
  is_archived: boolean;
  is_deleted: boolean;
  is_restorable: boolean;
  days_until_restore_expiry: number | null;
}
export interface TaskDeleteImpactOut {
  task_id: string;
  project_id: string;
  current_status: string;
  is_archived: boolean;
  is_deleted: boolean;
  would_block: boolean;
  block_reason: string | null;
}
export interface TaskRecycleBinEntry {
  task_id: string;
  project_id: string;
  title: string;
  status: string;
  deleted_at: string;
  delete_reason: string | null;
  deleted_by_admin_id: string | null;
  restorable_until: string;
  days_remaining: number;
}
export interface BatchTaskLifecycleResult {
  archived?: string[];
  deleted?: string[];
  skipped: { task_id: string; reason: string }[];
}
export const taskLifecycleApi = {
  get: (taskId: string): Promise<TaskLifecycleOut> =>
    request<TaskLifecycleOut>(`/api/v1/task-lifecycle/tasks/${taskId}`),
  archive: (taskId: string, reason?: string): Promise<TaskLifecycleOut> =>
    request<TaskLifecycleOut>(`/api/v1/task-lifecycle/tasks/${taskId}/archive`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? '' }),
    }),
  unarchive: (taskId: string): Promise<TaskLifecycleOut> =>
    request<TaskLifecycleOut>(`/api/v1/task-lifecycle/tasks/${taskId}/unarchive`, {
      method: 'POST', body: JSON.stringify({}),
    }),
  deleteImpact: (taskId: string): Promise<TaskDeleteImpactOut> =>
    request<TaskDeleteImpactOut>(`/api/v1/task-lifecycle/tasks/${taskId}/delete-impact`, {
      method: 'POST', body: JSON.stringify({}),
    }),
  delete: (taskId: string, reason?: string): Promise<TaskLifecycleOut> =>
    request<TaskLifecycleOut>(`/api/v1/task-lifecycle/tasks/${taskId}/delete`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? '' }),
    }),
  restore: (taskId: string): Promise<TaskLifecycleOut> =>
    request<TaskLifecycleOut>(`/api/v1/task-lifecycle/tasks/${taskId}/restore`, {
      method: 'POST', body: JSON.stringify({}),
    }),
  recycleBin: (limit = 50, offset = 0, projectId?: string): Promise<TaskRecycleBinEntry[]> => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (projectId) params.set('project_id', projectId);
    return request<TaskRecycleBinEntry[]>(`/api/v1/task-lifecycle/recycle-bin?${params.toString()}`);
  },
  batchArchive: (taskIds: string[], reason?: string): Promise<BatchTaskLifecycleResult> =>
    request<BatchTaskLifecycleResult>('/api/v1/task-lifecycle/batch/archive', {
      method: 'POST', body: JSON.stringify({ task_ids: taskIds, reason: reason ?? '' }),
    }),
  batchDelete: (taskIds: string[], reason?: string): Promise<BatchTaskLifecycleResult> =>
    request<BatchTaskLifecycleResult>('/api/v1/task-lifecycle/batch/delete', {
      method: 'POST', body: JSON.stringify({ task_ids: taskIds, reason: reason ?? '' }),
    }),
};

// --- v0.9.24 Provider Credential Test Path --------------------------------
export const providerCredentialTestApi = {
  test: (provider: string, payload: Record<string, unknown> = {}): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/providers/${provider}/test-credential`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  testGeneric: (payload: Record<string, unknown>): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>('/api/v1/providers/test-credential', {
      method: 'POST', body: JSON.stringify(payload),
    }),
};

export { ApiError };

// ─── v1.0-rc.4 — Provider Model Catalog ──────────────────────────────────

export interface CatalogProvider {
  provider: string;
  display_name: string;
  configured: boolean;
  key_count: number;
  fingerprint: string | null;
  model: string | null;
  base_url: string | null;
  status: string;
  last_used_at: string | null;
  is_custom: boolean;
  model_count: number;
  models_refreshed_at: string | null;
  auth_header: string | null;
  models_endpoint: string | null;
  test_status?: string;
  last_tested_at?: string | null;
  failure_reason?: string;
}

export interface CatalogProviderList {
  providers: CatalogProvider[];
}

export interface CatalogModel {
  id: string;
  model_id: string;
  display_name: string | null;
  source: string;
  is_favorite: boolean;
  test_status?: 'untested' | 'test_passed' | 'test_failed';
  last_tested_at?: string | null;
  latency_ms?: number | null;
  failure_reason?: string;
}

export interface CatalogModelList {
  provider: string;
  models: CatalogModel[];
  refreshed_at: string | null;
}

export interface ModelRefreshResult {
  provider: string;
  model_count: number;
  models: string[];
  error: string | null;
  refreshed_at: string;
}

export const providerCatalogApi = {
  listAll: (): Promise<CatalogProviderList> =>
    request<CatalogProviderList>('/api/v1/providers'),

  createCustom: (payload: {
    provider_name: string;
    display_name: string;
    base_url: string;
    api_key: string;
    auth_header?: string;
    models_endpoint?: string;
    chat_endpoint?: string;
    default_model?: string;
  }): Promise<CatalogProvider> =>
    request<CatalogProvider>('/api/v1/providers/custom', {
      method: 'POST', body: JSON.stringify(payload),
    }),

  updateConfig: (provider: string, payload: {
    base_url?: string;
    default_model?: string;
    models_endpoint?: string;
    auth_header?: string;
    api_key?: string;
  }): Promise<CatalogProvider> =>
    request<CatalogProvider>(`/api/v1/providers/${encodeURIComponent(provider)}/config`, {
      method: 'PUT', body: JSON.stringify(payload),
    }),

  getModels: (provider: string): Promise<CatalogModelList> =>
    request<CatalogModelList>(`/api/v1/providers/${encodeURIComponent(provider)}/models`),

  refreshModels: (provider: string): Promise<ModelRefreshResult> =>
    request<ModelRefreshResult>(`/api/v1/providers/${encodeURIComponent(provider)}/models/refresh`, {
      method: 'POST', body: JSON.stringify({}),
    }),

  testModel: (provider: string, modelId: string): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/providers/${encodeURIComponent(provider)}/models/${encodeURIComponent(modelId)}/test`, {
      method: 'POST', body: JSON.stringify({}),
    }),

  addCustomModel: (provider: string, payload: {
    model_id: string;
    display_name?: string;
    is_favorite?: boolean;
  }): Promise<CatalogModel> =>
    request<CatalogModel>(`/api/v1/providers/${encodeURIComponent(provider)}/models/custom`, {
      method: 'POST', body: JSON.stringify(payload),
    }),

  deleteCustomModel: (provider: string, modelId: string): Promise<void> =>
    request<void>(`/api/v1/providers/${encodeURIComponent(provider)}/models/custom/${encodeURIComponent(modelId)}`, {
      method: 'DELETE',
    }),

  deleteProvider: (provider: string): Promise<void> =>
    request<void>(`/api/v1/providers/${encodeURIComponent(provider)}`, {
      method: 'DELETE',
    }),

  saveKey: (provider: string, payload: {
    api_key: string;
    model?: string;
    base_url?: string;
  }): Promise<Record<string, unknown>> =>
    request<Record<string, unknown>>(`/api/v1/settings/providers/${encodeURIComponent(provider)}/key`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
};

// ─── v0.9.4 — Project Vault ─────────────────────────────────────────────────

export const vaultApi = {
  list: (projectId: string): Promise<import('./types').VaultResource[]> =>
    request<import('./types').VaultResource[]>(
      `/api/v1/projects/${projectId}/vault/resources`,
    ),
  create: (projectId: string, payload: import('./types').VaultResourceCreate): Promise<import('./types').VaultResource> =>
    request<import('./types').VaultResource>(
      `/api/v1/projects/${projectId}/vault/resources`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  get: (projectId: string, resourceId: string): Promise<import('./types').VaultResource> =>
    request<import('./types').VaultResource>(
      `/api/v1/projects/${projectId}/vault/resources/${resourceId}`,
    ),
  disable: (projectId: string, resourceId: string): Promise<import('./types').VaultResource> =>
    request<import('./types').VaultResource>(
      `/api/v1/projects/${projectId}/vault/resources/${resourceId}/disable`,
      { method: 'POST' },
    ),
};


// ─── v1.0 Step 1 — Project Blueprint ──────────────────────────────────
export const blueprintApi = {
  generate(projectId: string, requirement: string) {
    return request<any>(`/api/v1/projects/${projectId}/blueprint/generate`, {
      method: 'POST',
      body: JSON.stringify({ requirement }),
    });
  },
  get(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/blueprint`);
  },
  update(projectId: string, data: Record<string, unknown>) {
    return request<any>(`/api/v1/projects/${projectId}/blueprint`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  confirm(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/blueprint/confirm`, {
      method: 'POST',
      body: JSON.stringify({ confirmed_by: 'user' }),
    });
  },
  addChangeLog(projectId: string, data: Record<string, unknown>) {
    return request<any>(`/api/v1/projects/${projectId}/blueprint/change-log`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  getChangeLogs(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/blueprint/change-logs`);
  },
};

// ─── v1.0 Step 2 — Stage Plan + Task Packages ────────────────────────
export const stagePlanApi = {
  generate(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/blueprint/stage-plan/generate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  get(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/stage-plan/`);
  },
  update(projectId: string, data: Record<string, unknown>) {
    return request<any>(`/api/v1/projects/${projectId}/stage-plan/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  confirm(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/stage-plan/confirm`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  generateTaskPackages(projectId: string, stageId: string) {
    return request<any>(`/api/v1/projects/${projectId}/stages/${stageId}/task-packages/generate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  listTaskPackages(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/task-packages/`);
  },
  approveTaskPackage(projectId: string, taskPackageId: string) {
    return request<any>(`/api/v1/projects/${projectId}/task-packages/${taskPackageId}/approve`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  rejectTaskPackage(projectId: string, taskPackageId: string, reason: string) {
    return request<any>(`/api/v1/projects/${projectId}/task-packages/${taskPackageId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// ─── v1.0 Step 3 — Delivery Pipeline ────────────────────────────────
export const deliveryPipelineApi = {
  start(projectId: string, requirement: string) {
    return request<any>(`/api/v1/projects/${projectId}/delivery-pipeline/start`, {
      method: 'POST',
      body: JSON.stringify({ requirement }),
    });
  },
  get(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/delivery-pipeline`);
  },
  advance(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/delivery-pipeline/advance`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  status(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/delivery-pipeline/status`);
  },
};

// ─── v1.0 Step 4 — Execution Runs ────────────────────────────────────
export const executionRunApi = {
  create(data: { project_id: string; pipeline_id?: string; task_package_id: string; execution_channel: string; test_command?: string }) {
    return request<any>('/api/v1/execution-runs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  get(runId: string) {
    return request<any>(`/api/v1/execution-runs/${runId}`);
  },
  listByProject(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/execution-runs`);
  },
  start(runId: string) {
    return request<any>(`/api/v1/execution-runs/${runId}/start`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  cancel(runId: string) {
    return request<any>(`/api/v1/execution-runs/${runId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  getDiff(runId: string) {
    return request<any>(`/api/v1/execution-runs/${runId}/diff`);
  },
  reviewApprove(runId: string) {
    return request<any>(`/api/v1/execution-runs/${runId}/review/approve`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  reviewReject(runId: string, reason: string) {
    return request<any>(`/api/v1/execution-runs/${runId}/review/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
  reviewRequireFix(runId: string, reason: string) {
    return request<any>(`/api/v1/execution-runs/${runId}/review/require-fix`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
  codexStatus() {
    return request<any>('/api/v1/execution/codex-cli/status');
  },
  rerun(runId: string) {
    return request<any>(`/api/v1/execution-runs/${runId}/rerun`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  cleanupWorkspace(runId: string) {
    return request<any>(`/api/v1/execution-runs/${runId}/cleanup-workspace`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
};

// ─── v1.0 Step 6 — Final Delivery ──────────────────────────────────────
export const finalDeliveryApi = {
  review(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/final-delivery/review`, {
      method: 'POST', body: JSON.stringify({}),
    });
  },
  get(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/final-delivery`);
  },
  approve(projectId: string, deliveryMethod: string) {
    return request<any>(`/api/v1/projects/${projectId}/final-delivery/approve`, {
      method: 'POST', body: JSON.stringify({ delivery_method: deliveryMethod }),
    });
  },
  reject(projectId: string, reason: string) {
    return request<any>(`/api/v1/projects/${projectId}/final-delivery/reject`, {
      method: 'POST', body: JSON.stringify({ reason }),
    });
  },
  packagingPlan(projectId: string, method: string) {
    return request<any>(`/api/v1/projects/${projectId}/delivery-package/plan`, {
      method: 'POST', body: JSON.stringify({ method }),
    });
  },
  report(projectId: string) {
    return request<any>(`/api/v1/projects/${projectId}/delivery-report`);
  },
};

// ─── v0.9.25-rc4 Requirement Discovery + Model Roles (no Codex CLI HTTP) ─

export interface RequirementDiscoveryRequestPayload {
  mode: 'normal' | 'live';
  requirement: string;
  preferred_model?: string | null;
  fallback_models?: string[] | null;
}

export interface RequirementDiscoveryReport {
  business_goal: string;
  user_roles: string[];
  feature_list: string[];
  boundary_conditions: string[];
  phase_one: string[];
  phase_two: string[];
  risks: string[];
  follow_up_questions: string[];
}

export interface LiveRequirementCard {
  understood: string;
  target_user: string;
  demo_v1_focus: string[];
  demo_v1_skip: string[];
  biggest_risk: string;
  needs_confirm: string[];
}

export interface RequirementDiscoveryResponse {
  mode: 'normal' | 'live';
  discovery_type: string;
  report: RequirementDiscoveryReport | null;
  live_card: LiveRequirementCard | null;
}

export interface HandoverSummaryRequestPayload {
  task_type: string;
  failed_model: string;
  failed_role: string;
  failure_kind: string;
  failure_message: string;
  retry_attempts?: number;
  attempted_at: string;
  partial_output?: string | null;
  proposed_next_model: string;
  proposed_next_role: string;
  current_model?: string | null;
  failure_reason?: string | null;
  suggested_alternative?: string | null;
  project_goal?: string;
  task_goal?: string;
  done?: string[];
  todo?: string[];
  do_not_modify?: string[];
  acceptance_criteria?: string[];
  key_context?: string[];
  file_scope?: string[];
  live_mode?: boolean;
}

export interface HandoverSummaryResponse {
  generated_at: string;
  current_model: string;
  failure_kind: string;
  failure_reason: string;
  suggested_alternative: string;
  risk_level: string;
  can_auto_switch: boolean;
  live_mode: boolean;
  live_fallback_strategy: string | null;
  project_goal: string;
  task_goal: string;
  done: string[];
  todo: string[];
  do_not_modify: string[];
  acceptance_criteria: string[];
  key_context: string[];
  file_scope: string[];
}

export const requirementDiscoveryApi = {
  generate: (
    payload: RequirementDiscoveryRequestPayload,
  ): Promise<RequirementDiscoveryResponse> =>
    request<RequirementDiscoveryResponse>(
      '/api/v1/requirement-discovery',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
};

export const failoverApi = {
  handoverSummary: (
    payload: HandoverSummaryRequestPayload,
  ): Promise<HandoverSummaryResponse> =>
    request<HandoverSummaryResponse>(
      '/api/v1/model-failover/handover-summary',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
};

export const modelRolesApi = {
  get: (): Promise<import('./types').ModelRolePolicy> =>
    request<import('./types').ModelRolePolicy>('/api/v1/model-roles'),
  save: (
    payload: import('./types').ModelRolePolicyUpdate,
  ): Promise<import('./types').ModelRolePolicy> =>
    request<import('./types').ModelRolePolicy>(
      '/api/v1/model-roles',
      { method: 'PUT', body: JSON.stringify(payload) },
    ),
  defaults: (
    mode: import('./types').RequirementMode,
  ): Promise<import('./types').ModelRoleDefaults> =>
    request<import('./types').ModelRoleDefaults>(
      `/api/v1/model-roles/defaults?mode=${mode}`,
    ),
};

// ── Phase 1B.1: Codex Bridge API ──────────────────────────────────────────────

export const bridgeApi = {
  submitJob: (payload: { project_id: string; task_package: Record<string, unknown>; job_type: 'execute' }) =>
    request<{ job_id: string; task_id: string; run_id: string; status: string }>('/api/v1/bridge/jobs', { method: 'POST', body: JSON.stringify(payload) }),
  /** GET /api/v1/bridge/worker-status — current Bridge Worker status */
  workerStatus: (): Promise<import('./types').WorkerStatus> =>
    request<import('./types').WorkerStatus>('/api/v1/bridge/worker-status'),

  /** POST /api/v1/bridge/worker/pause — pause new job dispatch */
  pauseWorker: (reason?: string): Promise<{ paused: boolean; reason: string | null }> =>
    request('/api/v1/bridge/worker/pause', {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null }),
    }),

  /** POST /api/v1/bridge/worker/resume — resume job dispatch */
  resumeWorker: (): Promise<{ paused: boolean }> =>
    request('/api/v1/bridge/worker/resume', { method: 'POST' }),

  /** GET /api/v1/bridge/jobs/{jobId} — get Codex job status */
  getJob: (jobId: string): Promise<import('./types').CodexJob> =>
    request<import('./types').CodexJob>(`/api/v1/bridge/jobs/${jobId}`),

  /** GET /api/v1/bridge/jobs/{jobId}/logs — get job logs */
  getJobLogs: (
    jobId: string,
    offset = 0,
    limit = 200,
  ): Promise<import('./types').CodexJobLog[]> =>
    request<import('./types').CodexJobLog[]>(
      `/api/v1/bridge/jobs/${jobId}/logs?offset=${offset}&limit=${limit}`,
    ),

  /** POST /api/v1/bridge/jobs/{jobId}/cancel — request cancellation */
  cancelJob: (
    jobId: string,
    reason = 'user_requested',
  ): Promise<{ job_id: string; status: string; cancel_requested: boolean }> =>
    request(`/api/v1/bridge/jobs/${jobId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** GET /api/v1/bridge/jobs?project_id=... — list jobs for project */
  listJobs: (
    projectId: string,
    statusFilter?: string,
    limit = 50,
  ): Promise<Record<string, unknown>[]> => {
    const params = new URLSearchParams({ project_id: projectId, limit: String(limit) });
    if (statusFilter) params.set('status', statusFilter);
    return request(`/api/v1/bridge/jobs?${params}`);
  },
};

export const authApi = {
  me: () => request<{ username: string }>('/api/v1/auth/me'),
  login: (username: string, password: string) => request<{ username: string }>('/api/v1/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: () => request<void>('/api/v1/auth/logout', { method: 'POST' }),
  changePassword: (current_password: string, new_password: string, confirm_password: string) =>
    request<void>('/api/v1/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password, confirm_password }),
    }),
};
